/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * ⚠️ DEPRECATED: Legacy Interactive Story UI — DO NOT EDIT ⚠️
 * 
 * This screen is part of the deprecated interactive story journey.
 * It is NOT routed in production and kept only for backwards compatibility.
 * 
 * The LIVE production UI is the Hub System Assessment at /hub
 * 
 * DO NOT make changes to this file unless fixing security vulnerabilities.
 * All new features should be added to the Hub UI instead.
 */

/**
 * Screen 5: Comfort & Controls (Honesty Moment)
 * Explains that older boilers don't modulate and control upgrades can improve comfort
 * Includes links to technical appendices (Layer 3)
 */

import DeepDive from '../../components/DeepDive';
import { deepDives } from '../../content/deepDiveContent';
import { useStoryContext } from '../../context/StoryContext';
import { useRecommendation } from '../../context/RecommendationContext';
import { buildSummaryLine } from './reportUtils';

function Screen5ComfortControls() {
  const { openTechnicalAppendix } = useStoryContext();
  const { collectedData } = useRecommendation();
  const selectedEducation = collectedData?.storyEducationSelections ?? [];
  const showEducation = (id: string) =>
    selectedEducation.length === 0 || selectedEducation.includes(id);

  const comfortHighlights = [
    {
      label: 'Current thermostat',
      value: buildSummaryLine(collectedData?.currentThermostatType, collectedData?.includeThermostat),
    },
    {
      label: 'Preferred comfort style',
      value: buildSummaryLine(collectedData?.operatingStylePreference, collectedData?.heatingContinuityImportance),
    },
    {
      label: 'Smart control features',
      value: buildSummaryLine(collectedData?.wantLoadCompensation, collectedData?.wantSmartStart, collectedData?.wantMultiZone),
    },
    {
      label: 'Noise sensitivity',
      value: buildSummaryLine(collectedData?.noiseSensitivity),
    },
  ];

  return (
    <div className="interactive-story-screen">
      <h1 className="screen-title">Comfort & Controls</h1>

      <div className="screen-body">
        <div className="honesty-card">
          <div className="honesty-badge">💡 Honesty Moment</div>

          <div className="screen-text">
            Most older boilers don&apos;t modulate. They&apos;re either on full blast or off completely.
            <DeepDive
              trigger="Understand this better"
              content={deepDives.controlsExplained}
            />
          </div>

          <p className="screen-text">
            Upgrading your controls alone—without changing the boiler—can significantly improve comfort.
          </p>
        </div>

        <div className="report-section">
          <h2 className="report-section-title">Your comfort priorities</h2>
          <div className="report-section-grid">
            {comfortHighlights.map((detail) => (
              <div key={detail.label} className="report-section-card">
                <p className="report-section-label">{detail.label}</p>
                <p className="report-section-value">{detail.value}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="screen-text secondary">
          A new heating system mainly improves efficiency, hot water delivery, and long-term reliability. Comfort improvements come partly from the system, but largely from better controls.
        </p>

        {/* Technical deep dives */}
        <div className="technical-links-section">
          <p className="technical-links-intro">Explore the technical details:</p>
          <div className="technical-links-grid">
            {showEducation('modulation-cycling') && (
              <button
                className="technical-link-card"
                onClick={() => openTechnicalAppendix('modulation-cycling')}
              >
                <span className="link-icon">⚙️</span>
                <span className="link-text">Optional: Modulation vs Cycling</span>
              </button>
            )}
            {showEducation('clean-systems') && (
              <button
                className="technical-link-card"
                onClick={() => openTechnicalAppendix('clean-systems')}
              >
                <span className="link-icon">💧</span>
                <span className="link-text">Optional: Clean Systems & Efficiency</span>
              </button>
            )}
            {showEducation('power-flush-filters') && (
              <button
                className="technical-link-card"
                onClick={() => openTechnicalAppendix('power-flush-filters')}
              >
                <span className="link-icon">🧼</span>
                <span className="link-text">Optional: Power flushing & filters</span>
              </button>
            )}
            {showEducation('pipework-flow') && (
              <button
                className="technical-link-card"
                onClick={() => openTechnicalAppendix('pipework-flow')}
              >
                <span className="link-icon">🔧</span>
                <span className="link-text">Optional: Pipework & Flow</span>
              </button>
            )}
            {showEducation('stress-longevity') && (
              <button
                className="technical-link-card"
                onClick={() => openTechnicalAppendix('stress-longevity')}
              >
                <span className="link-icon">⏱️</span>
                <span className="link-text">Optional: Stress & Longevity</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Screen5ComfortControls;
